import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { deleteFile, postFile } from 'features/files/fileAPI';
import { FolderLocation, getStorageFolderName } from 'features/files/fileSlice';
import { FetchSiteParams, ISiteState } from 'features/site/siteSlice';
import { toAsciiId } from 'helpers/helpers';
import {
  deleteProjectData,
  fetchProjects,
  IProject,
  patchProjectData,
  postProjectData,
} from './projectAPI';

export interface IProjectsState {
  projects: IProject[];
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type ProjectsStateProps = {
  projects: IProject[];
};

export const initialState: IProjectsState = {
  projects: [],
  status: 'idle',
  errors: '',
};

export function getProjectAnchorId(
  project: IProject,
  useTitle = true,
  locale = 'nl',
) {
  return useTitle
    ? toAsciiId(
        locale === 'nl' ? (project.title_nl ?? '') : (project.title_en ?? ''),
      ) || `project-${project.id}`
    : `project-${project.id}`;
}

export const fetchProjectsAsync = createAsyncThunk(
  'project/fetchProjects',
  async (params: FetchSiteParams) => {
    const { forUsers } = params;
    const res = await fetchProjects(forUsers ? { all: true } : undefined);
    return { response: res };
  },
);

export const postProjectAsync = createAsyncThunk(
  'project/postProject',
  async (
    {
      data,
    }: {
      data: IProject;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postProjectData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchProjectAsync = createAsyncThunk(
  'project/patchProject',
  async (
    {
      data,
      file,
      project,
    }: {
      data: IProject;
      file?: File;
      project?: IProject;
    },
    thunkAPI,
  ) => {
    try {
      if (file || !data.image) {
        // we may be deleting the image
        // if there was an image, it needs to be deleted
        if (project?.image) {
          // 1.a from storage: TODO
          // 1.b from the db
          if (typeof project.image === 'object')
            await deleteFile(project.image.id);
          else await deleteFile(project.image);
        }
      }
      if (file) {
        // we have a new avatar
        // upload the new file
        const fd = await postFile(file, {
          description: 'Image for project ' + data.title_en,
          storage: getStorageFolderName(FolderLocation.Images),
          size: file.size,
          mimeType: file.type,
        });
        // keep the new file's id and uri
        data.image = { id: fd.data._id, uri: fd.data.message };
      }
      return { response: await patchProjectData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteProjectAsync = createAsyncThunk(
  'project/deleteProject',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deleteProjectData(id) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addProjectCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(fetchProjectsAsync.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(fetchProjectsAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.projects = action.payload.response.data;
    })
    .addCase(fetchProjectsAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(postProjectAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postProjectAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload?.response?.data?._id;
      if (id) {
        const data = { ...action.meta.arg.data, id };
        state.projects.push(data);
      }
    })
    .addCase(postProjectAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchProjectAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchProjectAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const p = state.projects.find((p) => p.id === action.meta.arg.data.id);
      if (p) {
        Object.assign(p, action.meta.arg.data);
      }
    })
    .addCase(patchProjectAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteProjectAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteProjectAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.projects = state.projects.filter(
        (p) => p.id !== action.meta.arg.id,
      );
    })
    .addCase(deleteProjectAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
